import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { candidateInfoSchema, type CandidateInfoFormData } from '../../schemas/candidate-info.schema'
import { useApplicationFormStore } from '../../stores/application-form-store'
import { useTranslation } from '../../hooks/useTranslation'

/**
 * CandidateInfoForm - Step 1 of the job application process
 */
export const CandidateInfoForm: React.FC = () => {
  const {
    candidateFullName,
    candidateEmail,
    candidatePhone,
    candidateLinkedIn,
    setCandidateInfo,
    nextStep
  } = useApplicationFormStore()
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isValid },
    watch
  } = useForm<CandidateInfoFormData>({
    resolver: zodResolver(candidateInfoSchema),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      fullName: candidateFullName || '',
      email: candidateEmail || '',
      phone: candidatePhone || '',
      linkedinUrl: candidateLinkedIn || ''
    }
  })

  const formValues = watch()

  const onSubmit = (data: CandidateInfoFormData) => {
    setCandidateInfo({
      candidateFullName: data.fullName,
      candidateEmail: data.email,
      candidatePhone: data.phone,
      candidateLinkedIn: data.linkedinUrl || null
    })
    nextStep()
  }

  const isFieldValid = (fieldName: keyof CandidateInfoFormData): boolean => {
    return touchedFields[fieldName] === true && !errors[fieldName] && !!formValues[fieldName]
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
      noValidate
    >
      {/* Full Name Field */}
      <div>
        <label
          htmlFor="fullName"
          className="block text-sm font-medium text-card-text mb-1"
        >
          {t('application.form.fullName')} <span className="text-error" aria-label="required">{t('common.labels.required')}</span>
        </label>
        <div className="relative">
          <input
            {...register('fullName')}
            id="fullName"
            type="text"
            className={`w-full px-4 py-3 min-h-[44px] text-base text-card-text border rounded-md focus:outline-none focus:ring-2 ${
              errors.fullName
                ? 'border-error focus:ring-error'
                : 'border-card-border focus:ring-primary'
            } ${isFieldValid('fullName') ? 'pr-10' : ''}`}
            placeholder={t('application.form.fullNamePlaceholder')}
            aria-required="true"
            aria-invalid={errors.fullName ? 'true' : 'false'}
            aria-describedby={errors.fullName ? 'fullName-error' : undefined}
          />
          {isFieldValid('fullName') && (
            <CheckCircle2
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary w-5 h-5"
              aria-hidden="true"
            />
          )}
        </div>
        {errors.fullName && (
          <p
            id="fullName-error"
            className="mt-1 text-sm text-error flex items-center gap-1"
            role="alert"
          >
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {errors.fullName.message}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-card-text mb-1"
        >
          {t('application.form.email')} <span className="text-error" aria-label="required">{t('common.labels.required')}</span>
        </label>
        <div className="relative">
          <input
            {...register('email')}
            id="email"
            type="email"
            className={`w-full px-4 py-3 min-h-[44px] text-base text-card-text border rounded-md focus:outline-none focus:ring-2 ${
              errors.email
                ? 'border-error focus:ring-error'
                : 'border-card-border focus:ring-primary'
            } ${isFieldValid('email') ? 'pr-10' : ''}`}
            placeholder={t('application.form.emailPlaceholder')}
            aria-required="true"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {isFieldValid('email') && (
            <CheckCircle2
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary w-5 h-5"
              aria-hidden="true"
            />
          )}
        </div>
        {errors.email && (
          <p
            id="email-error"
            className="mt-1 text-sm text-error flex items-center gap-1"
            role="alert"
          >
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Phone Field */}
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-card-text mb-1"
        >
          {t('application.form.phone')} <span className="text-error" aria-label="required">{t('common.labels.required')}</span>
        </label>
        <div className="relative">
          <input
            {...register('phone')}
            id="phone"
            type="tel"
            className={`w-full px-4 py-3 min-h-[44px] text-base text-card-text border rounded-md focus:outline-none focus:ring-2 ${
              errors.phone
                ? 'border-error focus:ring-error'
                : 'border-card-border focus:ring-primary'
            } ${isFieldValid('phone') ? 'pr-10' : ''}`}
            placeholder={t('application.form.phonePlaceholder')}
            aria-required="true"
            aria-invalid={errors.phone ? 'true' : 'false'}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
          />
          {isFieldValid('phone') && (
            <CheckCircle2
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary w-5 h-5"
              aria-hidden="true"
            />
          )}
        </div>
        {errors.phone && (
          <p
            id="phone-error"
            className="mt-1 text-sm text-error flex items-center gap-1"
            role="alert"
          >
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {errors.phone.message}
          </p>
        )}
      </div>

      {/* LinkedIn URL Field */}
      <div>
        <label
          htmlFor="linkedinUrl"
          className="block text-sm font-medium text-card-text mb-1"
        >
          {t('application.form.linkedIn')} <span className="text-card-text-secondary text-xs">{t('common.labels.optional')}</span>
        </label>
        <div className="relative">
          <input
            {...register('linkedinUrl')}
            id="linkedinUrl"
            type="url"
            className={`w-full px-4 py-3 min-h-[44px] text-base text-card-text border rounded-md focus:outline-none focus:ring-2 ${
              errors.linkedinUrl
                ? 'border-error focus:ring-error'
                : 'border-card-border focus:ring-primary'
            } ${isFieldValid('linkedinUrl') ? 'pr-10' : ''}`}
            placeholder={t('application.form.linkedInPlaceholder')}
            aria-invalid={errors.linkedinUrl ? 'true' : 'false'}
            aria-describedby={errors.linkedinUrl ? 'linkedinUrl-error' : undefined}
          />
          {isFieldValid('linkedinUrl') && (
            <CheckCircle2
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary w-5 h-5"
              aria-hidden="true"
            />
          )}
        </div>
        {errors.linkedinUrl && (
          <p
            id="linkedinUrl-error"
            className="mt-1 text-sm text-error flex items-center gap-1"
            role="alert"
          >
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
            {errors.linkedinUrl.message}
          </p>
        )}
      </div>

      {/* Next Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={!isValid}
          className={`px-6 py-3 min-h-[44px] min-w-[44px] rounded-md font-medium transition-colors ${
            isValid
              ? 'bg-primary text-surface hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2'
              : 'bg-gray-200 text-card-text-secondary cursor-not-allowed'
          }`}
          aria-disabled={!isValid}
        >
          {t('common.actions.next')}
        </button>
      </div>
    </form>
  )
}
